import { useState } from 'react'
import { useGetPayoutsQuery, useGetPayoutSummaryQuery } from '../../store/api/agentsApi'
import { AgentSearchDropdown, DateRangePicker } from '../../components/ui'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

export function AdminPayoutHistory() {
  const { data: summaryData } = useGetPayoutSummaryQuery()
  const developers = (summaryData?.summary ?? []).map(d => ({ id: d.developerId, name: d.developerName }))

  const [developerId, setDeveloperId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [offset, setOffset] = useState(0)

  const resetOffset = () => setOffset(0)

  const { data, isFetching, refetch } = useGetPayoutsQuery({
    developerId: developerId || undefined,
    from: from || undefined,
    to: to || undefined,
    offset,
  })
  const payouts = data?.payouts ?? []

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>🧾 Payout History</div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Every payout recorded from the Payouts tab, most recent first.
      </p>

      {/* Filters row */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <AgentSearchDropdown
          agents={developers}
          value={developerId}
          onChange={id => { setDeveloperId(id); resetOffset() }}
          placeholder="All developers"
          icon="👤"
          searchPlaceholder="Search developers…"
        />
        <DateRangePicker
          from={from} to={to}
          onFromChange={val => { setFrom(val); resetOffset() }}
          onToChange={val => { setTo(val); resetOffset() }}
        />
        {(from || to) && (
          <button
            onClick={() => { setFrom(''); setTo(''); resetOffset() }}
            style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Clear dates
          </button>
        )}
      </div>

      <div className={s.listCard}>
        {isFetching && payouts.length === 0 && <div className={s.listCardLoading}>Loading…</div>}
        {!isFetching && payouts.length === 0 && <div className={s.listCardEmpty}>No payouts recorded for these filters.</div>}
        {payouts.map((p, i) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 1rem', borderBottom: i < payouts.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{p.developerName}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                {fmtDate(p.periodFrom)} – {fmtDate(p.periodTo)}{p.note && ` · ${p.note}`}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-primary)' }}>{fmtRupees(p.amountPaise)}</div>
              {p.amountUsd != null && <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>${p.amountUsd.toFixed(2)}</div>}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', minWidth: '80px', textAlign: 'right', flexShrink: 0 }}>{fmtDate(p.paidAt)}</div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {(offset > 0 || data?.hasMore) && (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          {offset > 0 && (
            <button onClick={() => setOffset(Math.max(0, offset - 20))} style={{ padding: '0.35rem 0.85rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 7, fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>← Prev</button>
          )}
          {data?.hasMore && (
            <button onClick={() => setOffset(data.nextOffset ?? offset + 20)} style={{ padding: '0.35rem 0.85rem', background: 'var(--accent-primary)', border: 'none', borderRadius: 7, fontSize: '0.78rem', color: 'white', fontWeight: 600, cursor: 'pointer' }}>Next →</button>
          )}
        </div>
      )}
    </div>
  )
}
